/**
 * Public compute simulator — same economics as the private portal Simulator,
 * but styled as a dark home-page hero section with a clear CTA to /contact.
 * Let visitors *feel* what a $10-100k allocation means in compute output.
 */
import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cpu, Clock, TrendingUp, AlertTriangle, Brain, MessageSquare, Image as ImageIcon, Sparkles, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

// Same illustrative economics as the private simulator.
const H100_COST = 25000;
const HOURS_PER_MONTH = 720;
const UTIL_RATIO = 0.85;
const MARKET_RATE_HR = 3;
const YIELD_LOW = 0.15;
const YIELD_HIGH = 0.30;
const PRESETS = [10000, 25000, 50000, 100000];

const fmtInt = (n) => (isFinite(n) ? Math.round(n).toLocaleString("en-US").replace(/,/g, " ") : "0");

function computeAll(amount) {
  const a = Math.max(0, Number(amount) || 0);
  const h100 = a / H100_COST;
  const gpuHoursMonth = h100 * HOURS_PER_MONTH * UTIL_RATIO;
  const marketValueYear = gpuHoursMonth * 12 * MARKET_RATE_HR;
  return { h100, gpuHoursMonth, marketValueYear, revLow: a * YIELD_LOW, revHigh: a * YIELD_HIGH };
}

const COPY = {
  en: {
    eyebrow: "Interactive · Compute simulator",
    title: "See what your allocation would produce.",
    lead: "Drag the slider or pick a preset. See in real time the GPU-hours, H100-equivalent capacity and the indicative annual compute revenue range you would be exposed to.",
    presets: "Quick presets",
    label: "Amount",
    outputs: { hours: "GPU-hours / month", h100: "≈ H100-equivalent GPUs", market: "Market compute value / year", rev: "Indicative annual compute revenue" },
    useCasesTitle: "What that capacity could produce every month",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Full 70B model fine-tunes", d: "≈ 50 GPU-h per full pass." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "AI queries served", d: "≈ 1 000 GPT-scale queries / GPU-h." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "AI images generated", d: "≈ 50 hi-res images / GPU-h." },
      { icon: Sparkles, key: "training", base: 720, t: "Small model trainings", d: "≈ 720 GPU-h per 7B training." },
    ],
    footnote: "Illustrative only. Actual GPU-hours and compute revenues depend on project execution, market conditions, and the specific terms of your subscription. Assumptions: H100 ≈ $25 000, 85 % utilisation, $3/h blended market rate. Indicative annual compute revenue range: 15 % – 30 %.",
    ctaTitle: "Interested by these numbers?",
    ctaLead: "Investor access is by request only. Tell us about your objectives — our IR team responds within 24 h.",
    ctaBtn: "Request private access",
  },
  fr: {
    eyebrow: "Interactif · Simulateur de calcul",
    title: "Visualisez ce que votre allocation produirait.",
    lead: "Faites glisser le curseur ou choisissez un préréglage. Découvrez en temps réel les heures GPU, la capacité équivalente H100 et la fourchette indicative de revenus de calcul annuels auxquels vous seriez exposé·e.",
    presets: "Préréglages",
    label: "Montant",
    outputs: { hours: "Heures GPU / mois", h100: "≈ Équivalent GPU H100", market: "Valeur marché de calcul / an", rev: "Revenu de calcul annuel indicatif" },
    useCasesTitle: "Ce que cette capacité pourrait produire chaque mois",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Fine-tunes complets 70B", d: "≈ 50 h GPU par passe complète." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "Requêtes IA servies", d: "≈ 1 000 requêtes GPT / heure GPU." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "Images IA générées", d: "≈ 50 images HD / heure GPU." },
      { icon: Sparkles, key: "training", base: 720, t: "Entraînements de petits modèles", d: "≈ 720 h GPU pour un 7B." },
    ],
    footnote: "Illustratif uniquement. Les heures GPU et revenus de calcul réels dépendent de l'exécution, des conditions de marché et des termes propres à votre souscription. Hypothèses : H100 ≈ 25 000 $, 85 % d'utilisation, taux marché mixte 3 $/h. Fourchette indicative de revenus de calcul annuels : 15 % – 30 %.",
    ctaTitle: "Ces chiffres vous parlent ?",
    ctaLead: "L'accès investisseur se fait uniquement sur demande. Parlez-nous de vos objectifs — notre équipe IR répond sous 24 h.",
    ctaBtn: "Demander un accès privé",
  },
  es: {
    eyebrow: "Interactivo · Simulador de cálculo",
    title: "Descubre lo que tu asignación produciría.",
    lead: "Desliza el cursor o elige un preajuste. Ve en tiempo real las horas GPU, la capacidad equivalente H100 y el rango indicativo de ingresos anuales de cálculo al que estarías expuesto.",
    presets: "Preajustes",
    label: "Importe",
    outputs: { hours: "Horas GPU / mes", h100: "≈ GPU equivalentes H100", market: "Valor de mercado de cálculo / año", rev: "Ingreso anual de cálculo indicativo" },
    useCasesTitle: "Lo que esa capacidad podría producir cada mes",
    useCases: [
      { icon: Brain, key: "finetune", base: 50, t: "Fine-tunes completos 70B", d: "≈ 50 h GPU por pasada." },
      { icon: MessageSquare, key: "queries", base: 0.001, t: "Consultas IA servidas", d: "≈ 1 000 consultas GPT / hora GPU." },
      { icon: ImageIcon, key: "images", base: 0.02, t: "Imágenes IA generadas", d: "≈ 50 imágenes HD / hora GPU." },
      { icon: Sparkles, key: "training", base: 720, t: "Entrenamientos modelos pequeños", d: "≈ 720 h GPU por 7B." },
    ],
    footnote: "Solo ilustrativo. Las horas GPU e ingresos de cálculo reales dependen de la ejecución, condiciones de mercado y términos específicos. Hipótesis: H100 ≈ 25 000 $, 85 % de utilización, tarifa mercado mixta 3 $/h. Rango anual indicativo de ingresos de cálculo: 15 % – 30 %.",
    ctaTitle: "¿Te interesan estas cifras?",
    ctaLead: "El acceso a inversores es solo bajo solicitud. Cuéntanos tus objetivos — nuestro equipo IR responde en 24 h.",
    ctaBtn: "Solicitar acceso privado",
  },
};

export default function PublicSimulator() {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.en;
  const [amount, setAmount] = useState(25000);
  const r = useMemo(() => computeAll(amount), [amount]);
  const sliderPct = Math.min(100, Math.max(0, ((amount - 1000) / (250000 - 1000)) * 100));

  return (
    <section
      id="simulateur"
      data-testid="public-simulator-section"
      className="relative bg-[#0a0a0a] text-white border-t border-white/5 overflow-hidden"
    >
      {/* subtle radial glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_50%_0%,rgba(52,211,153,0.10),transparent_60%)]" />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium text-emerald-400">
            <span className="w-6 h-px bg-emerald-400/50" /> {c.eyebrow}
          </p>
          <h2 className="mt-4 font-display font-light tracking-tighter text-4xl md:text-6xl leading-[1.02]">
            {c.title}
          </h2>
          <p className="mt-6 text-[16px] leading-relaxed text-white/60 max-w-2xl">{c.lead}</p>
        </div>

        {/* Interactive area */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Input panel */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 backdrop-blur">
            <p className="text-[11px] tracking-widest uppercase text-white/50">{c.presets}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  data-testid={`home-sim-preset-${p}`}
                  onClick={() => setAmount(p)}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] tracking-wide border transition-colors ${
                    amount === p
                      ? "bg-emerald-500 text-black border-emerald-500 font-medium"
                      : "bg-transparent border-white/15 text-white/70 hover:text-white hover:border-white/30"
                  }`}
                >
                  ${p / 1000}k
                </button>
              ))}
            </div>

            <div className="mt-10">
              <p className="text-[11px] tracking-widest uppercase text-white/50">{c.label} (USD)</p>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-white/40 font-display text-3xl">$</span>
                <input
                  type="number"
                  min="1000"
                  step="1000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="home-sim-amount"
                  className="w-full font-display text-4xl md:text-5xl tracking-tighter bg-transparent outline-none py-1 border-b border-white/15 focus:border-emerald-400 transition-colors text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <input
                type="range"
                min="1000"
                max="250000"
                step="1000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="home-sim-range"
                className="mt-6 w-full h-1.5 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:shadow-lg [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400"
                style={{ background: `linear-gradient(to right, rgb(52 211 153) 0%, rgb(52 211 153) ${sliderPct}%, rgba(255,255,255,0.12) ${sliderPct}%, rgba(255,255,255,0.12) 100%)` }}
              />
              <div className="mt-2 flex justify-between text-[10px] tracking-widest uppercase text-white/40">
                <span>$1k</span>
                <span>$250k</span>
              </div>
            </div>
          </div>

          {/* Output panel */}
          <div className="lg:col-span-3 rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/[0.08] via-transparent to-sky-500/[0.08] p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <Metric icon={Clock} label={c.outputs.hours} value={fmtInt(r.gpuHoursMonth)} testId="home-sim-hours" />
              <Metric icon={Cpu} label={c.outputs.h100} value={r.h100.toFixed(2).replace(/\.00$/, "")} testId="home-sim-h100" />
              <Metric icon={TrendingUp} label={c.outputs.market} value={`$${fmtInt(r.marketValueYear)}`} testId="home-sim-market" />
              <Metric icon={TrendingUp} label={c.outputs.rev} value={`$${fmtInt(r.revLow)} — $${fmtInt(r.revHigh)}`} testId="home-sim-rev" accent />
            </div>

            <div className="mt-10 pt-8 border-t border-white/10">
              <p className="text-[11px] tracking-widest uppercase text-white/50">{c.useCasesTitle}</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {c.useCases.map((u, i) => {
                  const I = u.icon;
                  const qty = Math.floor(r.gpuHoursMonth / u.base);
                  return (
                    <motion.div
                      key={u.key}
                      initial={{ opacity: 0, y: 8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.06 }}
                      data-testid={`home-sim-usecase-${u.key}`}
                      className="rounded-xl p-4 flex items-start gap-3 bg-white/[0.03] border border-white/10"
                    >
                      <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                        <I className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-display text-2xl tracking-tighter text-white">{fmtInt(qty)}</p>
                        <p className="text-[13px] leading-snug text-white/70">{u.t}</p>
                        <p className="mt-1 text-[10px] text-white/40">{u.d}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footnote */}
        <div className="mt-8 flex items-start gap-3 text-[11px] leading-relaxed max-w-3xl text-white/40">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400/80" />
          <p>{c.footnote}</p>
        </div>

        {/* CTA to request access */}
        <div
          data-testid="home-sim-cta"
          className="mt-14 rounded-2xl border border-emerald-500/20 bg-gradient-to-r from-emerald-500/[0.06] via-emerald-500/[0.03] to-transparent p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
        >
          <div className="max-w-xl">
            <p className="font-display text-2xl md:text-3xl tracking-tight text-white">{c.ctaTitle}</p>
            <p className="mt-3 text-[14px] leading-relaxed text-white/60">{c.ctaLead}</p>
          </div>
          <Link
            to="/contact"
            data-testid="home-sim-cta-btn"
            className="inline-flex items-center justify-center gap-2 h-12 md:h-14 px-8 rounded-full bg-white text-black text-[13px] tracking-wide font-medium hover:bg-white/90 transition-colors whitespace-nowrap"
          >
            {c.ctaBtn} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

const Metric = ({ icon: I, label, value, testId, accent = false }) => (
  <div data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-white/50">
      <I className="w-3.5 h-3.5" /> {label}
    </div>
    <p className={`mt-3 font-display font-light tracking-tighter text-3xl md:text-4xl leading-none ${accent ? "text-emerald-400" : "text-white"}`}>
      {value}
    </p>
  </div>
);
